import { useCallback, useEffect, useState } from 'react';
import { LayoutComponent } from '../LayoutComponent';
import { EventTaskCompletedIcon, EventTaskFutureIcon, EventTaskUncompletedIcon } from '../../Icons/IconsLib';
import { Flex, ProgressCircle, Content, TableView, TableHeader, TableBody, Column, Row, Cell } from '@adobe/react-spectrum';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import useComonentReload from '../../../../hooks/UseComponentReload';
import { FPAData, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { CircleFill } from 'react-bootstrap-icons';
import { statusColor } from '../../../../utils/helperUtil';
import { ListEventRequest } from '../../../../services/soap/scheduler/requests/ListEventRequest';
import { GetSchedulerSettingsRequest, SettingsItemType } from '../../../../services/soap/scheduler/requests/GetSchedulerSettingsRequest';
import styles from './events_list.module.css';

interface EventsListComponentProps {
  selectedItem?: FPAData;
}

function EventsList({ selectedItem }: Readonly<EventsListComponentProps>) {
  const { t } = useTranslation();

  let columnsData = [
    {
      key: 'icon',
      name: '',
      width: 50,
    },
    {
      key: 'dateTo',
      name: t('date', { ns: 'layout_components' }),
      width: 170,
    },
    {
      key: 'solutionStatus',
      name: t('status', { ns: 'layout_components' }),
      width: null,
    },
    {
      key: 'title',
      name: t('subject', { ns: 'layout_components' }),
      width: null,
    },
    {
      key: 'userList',
      name: t('user', { ns: 'layout_components' }),
      width: null,
    },
    {
      key: 'entryTypeName',
      name: t('type_of_work', { ns: 'layout_components' }),
      width: null,
    },
  ];

  let columnsDataMobile = [
    {
      key: 'icon',
      name: '',
      width: 25,
    },
    {
      key: 'dateTo',
      name: t('date', { ns: 'layout_components' }),
      width: 170,
    },
    {
      key: 'solutionStatus',
      name: t('status', { ns: 'layout_components' }),
      width: 150,
    },
    {
      key: 'title',
      name: t('subject', { ns: 'layout_components' }),
      width: 250,
    },
    {
      key: 'userList',
      name: t('user', { ns: 'layout_components' }),
      width: 250,
    },
    {
      key: 'entryTypeName',
      name: t('type_of_work', { ns: 'layout_components' }),
      width: 150,
    },
  ];

  const [reloadComponent] = useComonentReload();
  const { schedulerService, store } = useDependency();
  const { isMobile } = useViewInfo();
  const [showLoader, setShowLoader] = useState(false);
  const [rowsData, setRowsData] = useState<any>([]);

  useEffect(() => {
    (async () => {
      getEventStatusList().then(async (statusArray: any) => {
        const [eventsFuture, eventsPastCompleted, eventsPastUncompleted] = await Promise.all([
          loadEventsFuturList(statusArray),
          loadEventsPastLineCompleted(statusArray),
          loadEventsPastLineUnCompleted(statusArray),
        ]);
        const mergeEvents = [...eventsFuture, ...eventsPastCompleted, ...eventsPastUncompleted];
        setRowsData(mergeEvents);
      });
    })();
  }, [selectedItem, reloadComponent]);

  const getEventStatusList = useCallback(async () => {
    const statusArray: any = [];
    let res: any = await schedulerService.getSchedulerSettings(new GetSchedulerSettingsRequest(store.Server, store.SessionId, '1', SettingsItemType.EVENT_ITEM_TYPE, 300, 0));
    //console.log('res', res);
    if (res.result === 'OK' && res.ITEMS.length > 0) {
      res?.ITEMS.forEach((status: any) => {
        const { ident, title, color } = status.ITEM;
        let colorString = statusColor(color);
        statusArray.push({ ident, title, color: colorString });
      });

      statusArray.unshift({ ident: 'All states', title: '', color: 'black' });
      return statusArray;
    }
  }, []);

  const loadEvents = async (statusArray: any, standardStatus: any, rangeStart: Date, rangeEnd: Date, eventType: string) => {
    try {
      setShowLoader(true);
      let queryParams = {
        userFilter: 1,
        orderBy1Desc: false,
        orderBy1Field: 'dateFrom',
        anyTime: 0,
        folderId: '',
        projectId: '',
        activityId: '',
        dateOfInterest: 0,
        standardStatus: standardStatus,
        rangeEnd: rangeEnd,
        rangeStart: rangeStart,
        groupUsers: 1,
        userId: store.UserId,
      };

      switch (selectedItem?.type) {
        case FPADataTypes.FOLDER:
          queryParams.folderId = String(selectedItem?.id);
          break;
        case FPADataTypes.PROJECT:
          queryParams.folderId = String(selectedItem?.parent_id);
          queryParams.projectId = String(selectedItem?.id);
          break;
        case FPADataTypes.ACTIVITY:
          queryParams.folderId = String(selectedItem?.parent?.parent_id);
          queryParams.projectId = String(selectedItem?.parent_id);
          queryParams.activityId = String(selectedItem?.id);
          break;
      }

      let eventRes: any = await schedulerService.listEvent(
        new ListEventRequest(store.Server, store.SessionId, {
          ...queryParams,
        })
      );

      if (eventRes?.result == 'OK' && eventRes.ITEMS) {
        let eventData: any = eventRes.ITEMS.ITEM.map((item: any) => ({
          id: item.ident + '_' + Math.floor(Math.random() * 10000),
          ident: item.ident,
          dateTo: moment(item.dateTo).format('MM/DD/YYYY | HH:mm A'),
          title: item.title,
          entryTypeName: item.entryTypeName,
          userList: item.userList,
          solutionStatus: getStatusColor(statusArray, item.solutionStatus),
          icon: getEventIcon(eventType),
        }));
        //setRowsData((prev: any) => [...prev, eventData]);
        return eventData;
      } else {
        //ToastQueue.info(t('no_results', { ns: 'layout_components' }), { timeout: 50 });
        return [];
      }
    } finally {
      setShowLoader(false);
    }
  };

  const loadEventsFuturList = async (statusArray: any) => {
    return loadEvents(statusArray, undefined, new Date(), new Date(2099, 11, 30, 22, 27, 7), 'future');
  };

  const loadEventsPastLineCompleted = async (statusArray: any) => {
    return loadEvents(statusArray, 1, new Date(1950, 11, 30, 22, 27, 7), new Date(), 'pastCompleted');
  };

  const loadEventsPastLineUnCompleted = async (statusArray: any) => {
    return loadEvents(statusArray, 0, new Date(1950, 11, 30, 22, 27, 7), new Date(), 'pastUnCompleted');
  };

  const getStatusColor = (statusArray: any, solutionStatus: any) => {
    const statusObj = statusArray.find((obj: any) => obj.ident == solutionStatus);
    return (
      <>
        <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={statusObj?.color} /> {statusObj?.title}
      </>
    );
  };

  const getEventIcon = (eventType: string) => {
    if (eventType === 'future') {
      return <EventTaskFutureIcon size={32} />;
    } else if (eventType === 'pastCompleted') {
      return <EventTaskCompletedIcon size={32} />;
    } else if (eventType === 'pastUnCompleted') {
      return <EventTaskUncompletedIcon size={32} />;
    }
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={'center'} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <Flex direction={'column'} gap={'size-150'} position={'relative'} width={'100%'}>
        <Flex direction={'row'} alignItems={'start'} justifyContent={'start'}>
          <Content position={'relative'} UNSAFE_className={styles.heading_text}>
            Event List
          </Content>
        </Flex>
        <Flex direction={'column'}>
          <Flex maxHeight={{ base: '1000px', L: '450px', M: '450px' }} width="100%" direction="column" UNSAFE_style={{ overflowX: 'auto' }}>
            <TableView
              UNSAFE_className="tblLayoutComponent"
              aria-label="Event Future"
              selectionMode="single"
              selectionStyle="highlight"
              width={{ base: '100%', L: '100%', M: '100%' }}
              minHeight={'80px'}
              maxHeight={{ base: '1000px', L: '550px' }}
            >
              <TableHeader columns={isMobile ? columnsDataMobile : columnsData}>
                {column => (
                  <Column showDivider key={column.key} width={column?.width}>
                    {column.name}
                  </Column>
                )}
              </TableHeader>
              <TableBody items={rowsData}>{item => <Row>{columnKey => <Cell>{(item as any)[String(columnKey)]}</Cell>}</Row>}</TableBody>
            </TableView>
          </Flex>
        </Flex>
      </Flex>
    );
  }
}

export const EventsListComponent = LayoutComponent(EventsList);
